import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { ApiError, useApiFetch } from "../../api/client";
import { PageFooter } from "../../app/PageFooter";
import { TopBar } from "../../app/TopBar";
import { useAppUser } from "../../app/AppUserContext";
import { Btn } from "../../components/ui/Button";
import { Card } from "../../components/ui/Card";
import { Chip } from "../../components/ui/Chip";
import { PageSectionHeader } from "../../components/ui/PageSectionHeader";
import { useStickyHeaderHeight } from "../../components/useStickyHeaderHeight";
import { errorMessage, recordedLabel } from "../cockpit/format";
import { HowARowIsRead } from "./upload/HowARowIsRead";
import { PendingCodes } from "./upload/PendingCodes";
import { ResinDatabase } from "./database/ResinDatabase";
import { useNomenclatureDatabase } from "./database/useNomenclatureDatabase";
import { nomenclatureNumbers } from "./overview/nomenclatureNumbers";
import { NomenclatureStats } from "./overview/NomenclatureStats";
import { NomenclatureTabs, type NomenclatureTab } from "./overview/NomenclatureTabs";
import { UPLOAD_SECTION_IDS, uploadSteps } from "./overview/uploadStepStates";
import { UploadSteps } from "./overview/UploadSteps";
import { pluralRows } from "./upload/rowStatus";
import { TdsBulkUpload } from "./tds/TdsBulkUpload";
import type {
  ApprovalResult,
  CodeApproval,
  NomenclatureUpload,
  PendingCode,
  RejectionResult,
  ResinFamily,
} from "./types";
import { UploadDropzone } from "./ui/UploadDropzone";
import { NomenclatureFileGuide } from "./ui/UploadGuides";
import { UploadPreview } from "./upload/UploadPreview";
import { UploadStatusBar, type StatusTone } from "./ui/UploadStatusBar";
import { useHeldTdsFiles } from "./upload/useHeldTdsFiles";
import { PATHS } from "../../app/paths";

interface DuplicateUploadDetail {
  message?: string;
  existingBatchGuid?: string;
}

interface PageStatus {
  text: string;
  tone: StatusTone;
  detail?: string;
}

const NOMENCLATURE_FILE_SPECS = [".xlsx", "Up to 60 MB", "One grade per row"];
const TDS_FILE_SPECS = ["PDF", "Up to 60 MB each", "Many at once"];
const SECTION_CLASSES = "flex flex-col min-w-0 scroll-mt-[calc(var(--page-header-h,112px)+20px)]";

export function NomenclaturePage() {
  const navigate = useNavigate();
  const { appRole } = useAppUser();
  const canWrite = appRole === "Uploader" || appRole === "Admin";
  const apiFetch = useApiFetch();
  const apiFetchRef = useRef(apiFetch);
  apiFetchRef.current = apiFetch;
  const [searchParams, setSearchParams] = useSearchParams();
  const openUploadGuid = searchParams.get("upload");
  const activeTab: NomenclatureTab = searchParams.get("tab") === "database" ? "database" : "upload";
  const pendingCodesRef = useRef<HTMLElement>(null);
  const headerRef = useRef<HTMLDivElement>(null);
  useStickyHeaderHeight(headerRef, "--page-header-h");

  const updateSearchParams = useCallback(
    (changes: Record<string, string | null>) =>
      setSearchParams((currentParams) => {
        const nextParams = new URLSearchParams(currentParams);
        for (const [name, value] of Object.entries(changes)) {
          if (value === null) nextParams.delete(name);
          else nextParams.set(name, value);
        }
        return nextParams;
      }),
    [setSearchParams],
  );
  const selectTab = (tab: NomenclatureTab) => updateSearchParams({ tab: tab === "upload" ? null : tab });
  const openUpload = (batchGuid: string) => updateSearchParams({ upload: batchGuid });
  const showPendingCodes = () => {
    selectTab("upload");
    window.setTimeout(() => pendingCodesRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 0);
  };

  const [upload, setUpload] = useState<NomenclatureUpload | null>(null);
  const [pendingCodes, setPendingCodes] = useState<PendingCode[]>([]);
  const [resinFamilies, setResinFamilies] = useState<ResinFamily[]>([]);
  const [pageStatus, setPageStatus] = useState<PageStatus | null>(null);
  const [duplicateUploadGuid, setDuplicateUploadGuid] = useState<string | null>(null);
  const [isBusy, setIsBusy] = useState(false);
  const [databaseRefreshCount, setDatabaseRefreshCount] = useState(0);
  const refreshDatabase = useCallback(() => setDatabaseRefreshCount((count) => count + 1), []);
  const tdsActions = useHeldTdsFiles(upload, refreshDatabase);
  const database = useNomenclatureDatabase(databaseRefreshCount);
  const numbers = useMemo(
    () =>
      database.designations && database.gradeLinks && database.approvedCodes
        ? nomenclatureNumbers(database.designations, database.gradeLinks, database.approvedCodes)
        : null,
    [database.designations, database.gradeLinks, database.approvedCodes],
  );

  const loadPendingCodes = useCallback(async () => {
    const response = await apiFetchRef.current("nomenclature/pending-codes");
    setPendingCodes(await response.json());
  }, []);

  const loadUpload = useCallback(async (batchGuid: string) => {
    const response = await apiFetchRef.current(`nomenclature/uploads/${batchGuid}`);
    setUpload(await response.json());
  }, []);

  const refreshEverything = useCallback(
    async (batchGuid: string | null) => {
      await Promise.all([loadPendingCodes(), batchGuid ? loadUpload(batchGuid) : Promise.resolve()]);
      setDatabaseRefreshCount((count) => count + 1);
    },
    [loadPendingCodes, loadUpload],
  );

  useEffect(() => {
    apiFetchRef
      .current("reference/resin-taxonomy")
      .then((response) => response.json())
      .then(setResinFamilies)
      .catch(() => setResinFamilies([]));
  }, []);

  useEffect(() => {
    refreshEverything(openUploadGuid).catch((error) => setPageStatus({ text: errorMessage(error), tone: "bad" }));
    if (!openUploadGuid) setUpload(null);
  }, [openUploadGuid, refreshEverything]);

  const uploadFile = async (files: FileList) => {
    const file = files[0];
    if (!file || isBusy) return;
    setIsBusy(true);
    setPageStatus({ text: `Checking ${file.name}…`, tone: "info" });
    setDuplicateUploadGuid(null);
    try {
      const form = new FormData();
      form.append("file", file);
      const response = await apiFetchRef.current("nomenclature/uploads", { method: "POST", body: form });
      const createdUpload: NomenclatureUpload = await response.json();
      setUpload(createdUpload);
      openUpload(createdUpload.batchGuid);
      setPageStatus({
        text: `Checked ${pluralRows(createdUpload.rows.length)} from ${file.name}.`,
        tone: "ok",
        detail: "Stored with its upload batch, so the same file can't be loaded twice. Review the rows below, then save.",
      });
    } catch (error) {
      if (error instanceof ApiError && error.status === 409) {
        const detail = (error.body as { detail?: DuplicateUploadDetail } | null)?.detail;
        setDuplicateUploadGuid(detail?.existingBatchGuid ?? null);
        setPageStatus({ text: detail?.message ?? "This exact file was already uploaded.", tone: "bad" });
      } else {
        setPageStatus({ text: errorMessage(error), tone: "bad" });
      }
    } finally {
      setIsBusy(false);
    }
  };

  const runAction = async (action: () => Promise<string>) => {
    if (isBusy) return;
    setIsBusy(true);
    try {
      setPageStatus({ text: await action(), tone: "ok" });
      await refreshEverything(openUploadGuid);
    } catch (error) {
      setPageStatus({ text: errorMessage(error), tone: "bad" });
    } finally {
      setIsBusy(false);
    }
  };

  const saveUpload = () =>
    runAction(async () => {
      if (!upload) return "";
      const response = await apiFetchRef.current(`nomenclature/uploads/${upload.batchGuid}/save`, {
        method: "POST",
      });
      const savedUpload: NomenclatureUpload = await response.json();
      const savedCount = (savedUpload.rowCounts.Saved ?? 0) + (savedUpload.rowCounts.Existing ?? 0);
      return `Saved. ${pluralRows(savedCount)} are in the database.`;
    });

  const discardUpload = () =>
    runAction(async () => {
      if (!upload) return "";
      await apiFetchRef.current(`nomenclature/uploads/${upload.batchGuid}/discard`, { method: "POST" });
      return "Upload discarded. Nothing from it was saved.";
    });

  const approveCodes = (approvals: CodeApproval[]) =>
    runAction(async () => {
      const response = await apiFetchRef.current("nomenclature/pending-codes/approve", {
        method: "POST",
        body: JSON.stringify({ codes: approvals }),
      });
      const result: ApprovalResult = await response.json();
      const parts = [`${result.codesApproved} ${result.codesApproved === 1 ? "code" : "codes"} approved.`];
      if (result.rowsSaved) parts.push(`${pluralRows(result.rowsSaved)} saved.`);
      if (result.rowsNowReady) parts.push(`${pluralRows(result.rowsNowReady)} now ready to save.`);
      return parts.join(" ");
    });

  const rejectCode = (pendingCode: PendingCode, rejectionReason: string) =>
    runAction(async () => {
      const response = await apiFetchRef.current("nomenclature/pending-codes/reject", {
        method: "POST",
        body: JSON.stringify({
          standardCode: pendingCode.standardCode,
          slotName: pendingCode.slotName,
          code: pendingCode.code,
          rejectionReason,
        }),
      });
      const result: RejectionResult = await response.json();
      return `${pendingCode.code} rejected. ${pluralRows(result.rowsRejected)} marked rejected.`;
    });

  return (
    <>
      <div ref={headerRef} className="sticky top-0 z-20 bg-rs-black">
        <TopBar title="Resin Nomenclature">
          <Btn variant="ghost" onClick={() => navigate(PATHS.cockpit)}>
            <span className="material-symbols-sharp">arrow_back</span>Consultant Cockpit
          </Btn>
        </TopBar>
        <NomenclatureTabs activeTab={activeTab} pendingCodeCount={pendingCodes.length} onSelect={selectTab} />
      </div>

      <div className="bg-rs-black">
        <main className="flex flex-col gap-7 max-w-[1440px] mx-auto px-7 pt-7 pb-24 max-[700px]:px-4">
          <section className="flex flex-col gap-1.5">
            <h1 className="m-0 text-2xl font-semibold text-white">Resin Nomenclature</h1>
            <p className="m-0 max-w-[70ch] text-sm text-[#a9b0a1]">
              Upload grades with their RS designations, approve new codes and attach TDS files.
            </p>
          </section>
          <NomenclatureStats
            numbers={numbers}
            pendingCodeCount={pendingCodes.length}
            onShowPendingCodes={showPendingCodes}
          />

          {activeTab === "upload" ? (
            <div className="grid grid-cols-[240px_minmax(0,1fr)] gap-10 items-start max-[1000px]:grid-cols-1 max-[1000px]:gap-5">
              <UploadSteps steps={uploadSteps(upload, pendingCodes.length)} canWrite={canWrite} />

              <div className="flex flex-col gap-14 min-w-0">
                <section id={UPLOAD_SECTION_IDS.upload} className={SECTION_CLASSES} aria-labelledby="upload-heading">
                  <PageSectionHeader
                    headingId="upload-heading"
                    group="Step 1"
                    title="Upload nomenclature file"
                    description="Add or update grades and their RS designations. Every row is checked before anything is saved."
                  >
                    {NOMENCLATURE_FILE_SPECS.map((spec) => (
                      <Chip key={spec}>{spec}</Chip>
                    ))}
                  </PageSectionHeader>
                  <Card className="@container flex flex-col gap-3.5">
                    {canWrite ? (
                      <div className="grid gap-4 @min-[720px]:grid-cols-[minmax(0,1.15fr)_minmax(0,1fr)]">
                        <UploadDropzone
                          accept=".xlsx"
                          fileTypeLabel="XLSX"
                          title="Drop the spreadsheet here"
                          subtitle="or choose it from your computer"
                          buttonLabel="Choose a File"
                          onFiles={uploadFile}
                        />
                        <NomenclatureFileGuide />
                      </div>
                    ) : (
                      <UploadStatusBar tone="info">
                        Viewers can look at uploads and the database but can't upload, save or approve.
                      </UploadStatusBar>
                    )}
                    {pageStatus && (
                      <UploadStatusBar tone={pageStatus.tone} detail={pageStatus.detail}>
                        {pageStatus.text}
                        {duplicateUploadGuid && (
                          <button
                            type="button"
                            className="ml-2 bg-transparent border-none p-0 text-rs-green hover:underline cursor-pointer"
                            onClick={() => openUpload(duplicateUploadGuid)}
                          >
                            Open That Upload
                          </button>
                        )}
                      </UploadStatusBar>
                    )}
                  </Card>
                </section>

                <section id={UPLOAD_SECTION_IDS.check} className={SECTION_CLASSES} aria-labelledby="check-heading">
                  <PageSectionHeader
                    headingId="check-heading"
                    group="Steps 2 and 3"
                    title="Check before saving"
                    description="Each row gets a result. Save the valid rows, or discard the upload and fix the file."
                  >
                    {upload && (
                      <>
                        <Chip>{upload.fileName}</Chip>
                        <span className="text-rs-light-gray text-[12.5px]">
                          Uploaded by {upload.uploadedByDisplayName} · {recordedLabel(upload.uploadedUtc)}
                        </span>
                      </>
                    )}
                  </PageSectionHeader>
                  <Card>
                    {upload ? (
                      <UploadPreview
                        upload={upload}
                        canWrite={canWrite}
                        isBusy={isBusy}
                        tdsActions={tdsActions}
                        onSave={saveUpload}
                        onDiscard={discardUpload}
                      />
                    ) : (
                      <p className="m-0 text-rs-light-gray text-[13px] border border-dashed border-rs-jet rounded-md p-4 text-center">
                        Upload a file to see each row's result. Nothing is saved until you save.
                      </p>
                    )}
                  </Card>
                </section>

                <section
                  id={UPLOAD_SECTION_IDS.pending}
                  ref={pendingCodesRef}
                  className={SECTION_CLASSES}
                  aria-labelledby="pending-heading"
                >
                  <PageSectionHeader
                    headingId="pending-heading"
                    group="Step 4"
                    title="Pending codes"
                    description="New codes wait here. Approving a code saves every row that was waiting on it."
                  >
                    <Chip variant={pendingCodes.length ? "info" : "default"}>{pendingCodes.length}</Chip>
                  </PageSectionHeader>
                  <Card>
                    <PendingCodes
                      pendingCodes={pendingCodes}
                      resinFamilies={resinFamilies}
                      canApprove={canWrite}
                      isBusy={isBusy}
                      onApprove={approveCodes}
                      onReject={rejectCode}
                    />
                  </Card>
                </section>

                {canWrite && (
                  <section id={UPLOAD_SECTION_IDS.tds} className={SECTION_CLASSES} aria-labelledby="tds-heading">
                    <PageSectionHeader
                      headingId="tds-heading"
                      group="TDS files"
                      title="Upload TDS files"
                      description="Attach technical data sheets to saved grades. Each file is matched to its grade for you."
                    >
                      {TDS_FILE_SPECS.map((spec) => (
                        <Chip key={spec}>{spec}</Chip>
                      ))}
                    </PageSectionHeader>
                    <Card className="@container">
                      <TdsBulkUpload onAttached={refreshDatabase} />
                    </Card>
                  </section>
                )}

                <section id={UPLOAD_SECTION_IDS.rowGuide} className={SECTION_CLASSES} aria-labelledby="row-guide-heading">
                  <PageSectionHeader
                    headingId="row-guide-heading"
                    group="Reference"
                    title="How a row is read"
                    description="What happens to each spreadsheet row, in order."
                  />
                  <Card className="w-fit max-w-full min-w-0">
                    <HowARowIsRead />
                  </Card>
                </section>
              </div>
            </div>
          ) : (
            <section className="flex flex-col min-w-0" aria-labelledby="database-heading">
              <PageSectionHeader
                headingId="database-heading"
                group="Database"
                title="In the database by resin"
                description="Pick a resin to see its RS codes and the grades attached to each one. Use Base to narrow it, and ▶ on a grade for its details and TDS files."
              />
              <Card className="min-w-0">
                <ResinDatabase resinFamilies={resinFamilies} database={database} canWrite={canWrite} />
              </Card>
            </section>
          )}
        </main>
      </div>

      <PageFooter />
    </>
  );
}
